// src/hooks/useNotifications.js
import { useState, useCallback } from 'react';

/**
 * Custom hook for managing notifications/alerts
 * @returns {Object} Notification state and handlers
 */
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);

  // Add a new notification
  const addNotification = useCallback((type, message, duration = 5000, options = {}) => {
    const notification = {
      id: Date.now() + Math.random(),
      type, // 'success', 'error', 'warning', 'info'
      message,
      duration,
      timestamp: new Date(),
      ...options
    };

    setNotifications(prev => [...prev, notification]);
    setCurrentNotification(notification);

    // Auto-remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(notification.id);
      }, duration);
    }

    return notification.id;
  }, []);

  // Remove a specific notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    setCurrentNotification(prev => prev?.id === id ? null : prev);
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setCurrentNotification(null);
  }, []);

  // Specific notification types
  const showSuccess = useCallback((message, duration = 3000, options = {}) => {
    return addNotification('success', message, duration, options);
  }, [addNotification]);

  const showError = useCallback((message, duration = 5000, options = {}) => {
    return addNotification('error', message, duration, options);
  }, [addNotification]);

  const showWarning = useCallback((message, duration = 4000, options = {}) => {
    return addNotification('warning', message, duration, options);
  }, [addNotification]);

  const showInfo = useCallback((message, duration = 3000, options = {}) => {
    return addNotification('info', message, duration, options);
  }, [addNotification]);

  // Handle API response notifications
  const handleApiResponse = useCallback((response, successMessage, errorMessage) => {
    if (response.success) {
      showSuccess(response.message || successMessage);
    } else {
      showError(response.message || errorMessage);
    }
    return response;
  }, [showSuccess, showError]);

  // Handle async operation with notifications
  const withNotification = useCallback(async (asyncFn, loadingMessage, successMessage, errorMessage) => {
    let loadingId = null;
    
    if (loadingMessage) {
      loadingId = addNotification('info', loadingMessage, 0, { loading: true });
    }

    try {
      const result = await asyncFn();
      
      if (loadingId) {
        removeNotification(loadingId);
      }
      
      if (result.success !== false) {
        showSuccess(result.message || successMessage);
      } else {
        showError(result.message || errorMessage);
      }
      
      return result;
    } catch (error) {
      if (loadingId) {
        removeNotification(loadingId);
      }
      
      const message = error.response?.data?.message || error.message || errorMessage;
      showError(message);
      
      throw error;
    }
  }, [addNotification, removeNotification, showSuccess, showError]);

  // Get notification by type
  const getNotificationsByType = useCallback((type) => {
    return notifications.filter(n => n.type === type);
  }, [notifications]);

  // Check if there are any notifications of a specific type
  const hasNotificationType = useCallback((type) => {
    return notifications.some(n => n.type === type);
  }, [notifications]);

  // Get the latest notification
  const getLatestNotification = useCallback(() => {
    return notifications.length > 0 ? notifications[notifications.length - 1] : null;
  }, [notifications]);

  return {
    // State
    notifications,
    currentNotification,
    
    // Basic handlers
    addNotification,
    removeNotification,
    clearNotifications,
    
    // Specific notification types
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // Advanced handlers
    handleApiResponse,
    withNotification,
    
    // Utilities
    getNotificationsByType,
    hasNotificationType,
    getLatestNotification,
    
    // Computed values
    hasNotifications: notifications.length > 0,
    notificationCount: notifications.length
  };
};

/**
 * Hook for simple success/error state management
 * @returns {Object} Simple notification state and handlers
 */
export const useSimpleNotifications = () => {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successType, setSuccessType] = useState('');

  const showSuccess = useCallback((message, type = 'success') => {
    setSuccessMessage(message);
    setSuccessType(type);
    setErrorMessage('');
    
    // Auto-clear after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
      setSuccessType('');
    }, 3000);
  }, []);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setSuccessMessage('');
    setSuccessType('');
  }, []);

  const clearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
    setSuccessType('');
  }, []);

  return {
    successMessage,
    errorMessage,
    successType,
    showSuccess,
    showError,
    clearMessages,
    hasSuccess: !!successMessage,
    hasError: !!errorMessage
  };
};